/**
 * Bookings list — the tabular counterpart to the Gantt, for when you want to
 * search rather than scan.
 *
 * The status filter and search are rendered by the Fleet page, in the same
 * filter card the other tabs use, and passed in.
 */

import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { AlertTriangle, CalendarClock } from 'lucide-react';

import { Skeleton } from '../ui/skeleton';
import {
  bookingStatusLabel,
  bookingTypeLabel,
  bookingVehicleId,
  estimateCharge,
  formatCurrency,
  isLatePickup,
  isOpenEnded,
  isOverdue,
  vehicleLabel
} from '../../lib/fleet';
import { BOOKING_TONE_STYLES, bookingTone } from './fleetStyles';
import { cn } from '../../lib/utils';

export const BOOKING_FILTERS = [
  { value: 'current', label: 'Current' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Past' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All' }
];

const customerName = (booking) => {
  const c = booking.customer;
  if (typeof c === 'object' && c) return `${c.firstName || ''} ${c.lastName || ''}`.trim();
  return booking.driverName || booking.title || '';
};

const FleetBookingsTable = ({
  bookings,
  vehicles,
  loading,
  filter = 'current',
  search = '',
  now = new Date(),
  priv = { name: (v) => v, rego: (v) => v },
  onSelect
}) => {
  const vehicleById = useMemo(() => new Map(vehicles.map((v) => [v.$id, v])), [vehicles]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();

    return bookings
      .filter((b) => {
        const start = new Date(b.scheduledStart);
        const openEnded = isOpenEnded(b);
        const end = openEnded ? null : new Date(b.scheduledEnd);

        if (filter === 'current') {
          if (['cancelled', 'no_show', 'returned'].includes(b.status)) return false;
          return b.status === 'out' || (start <= now && end >= now) || start <= now;
        }
        if (filter === 'upcoming') return b.status === 'reserved' && start > now;
        if (filter === 'past') return b.status === 'returned' || (end && end < now && b.status !== 'out');
        if (filter === 'cancelled') return ['cancelled', 'no_show'].includes(b.status);
        return true;
      })
      .filter((b) => {
        if (!term) return true;
        const vehicle = vehicleById.get(bookingVehicleId(b));
        return [customerName(b), b.title, vehicle?.registration, vehicle?.displayName, b.notes]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(term));
      })
      .sort((a, b) => new Date(b.scheduledStart) - new Date(a.scheduledStart));
  }, [bookings, filter, search, now, vehicleById]);

  return (
    <div className="p-4">
      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center">
          <CalendarClock className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No bookings match this view.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Customer</th>
                <th className="px-3 py-2 font-medium">Vehicle</th>
                <th className="px-3 py-2 font-medium">Pickup</th>
                <th className="px-3 py-2 font-medium">Due back</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Charge</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((booking) => {
                const vehicle = vehicleById.get(bookingVehicleId(booking));
                const tone = BOOKING_TONE_STYLES[bookingTone(booking, now)];
                const overdue = isOverdue(booking, now);
                const late = isLatePickup(booking, now);
                const charge = estimateCharge(booking);

                return (
                  <tr
                    key={booking.$id}
                    onClick={() => onSelect?.(booking)}
                    className="cursor-pointer border-b transition-colors last:border-b-0 hover:bg-accent/50"
                  >
                    <td className="px-3 py-2.5">
                      <div className="font-medium">{priv.name(customerName(booking)) || '—'}</div>
                      <div className="text-xs text-muted-foreground">{bookingTypeLabel(booking.type)}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      {vehicle ? (
                        <>
                          <div>{vehicleLabel(vehicle)}</div>
                          <div className="font-mono text-xs text-muted-foreground">{priv.rego(vehicle.registration)}</div>
                        </>
                      ) : (
                        <span className="text-xs italic text-muted-foreground">Not assigned</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {format(new Date(booking.scheduledStart), 'd MMM yyyy')}
                      {late && (
                        <span className="ml-1 text-xs text-amber-600 dark:text-amber-400" title="Pickup time has passed">
                          late
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {isOpenEnded(booking) ? (
                        <span className="text-muted-foreground">Ongoing</span>
                      ) : (
                        format(new Date(booking.scheduledEnd), 'd MMM yyyy')
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium', tone.bar)}>
                        {overdue && <AlertTriangle className="h-3 w-3" />}
                        {overdue ? 'Overdue' : bookingStatusLabel(booking.status)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {booking.chargeToCustomer === false ? (
                        <span className="text-xs text-muted-foreground">Free</span>
                      ) : (
                        formatCurrency(booking.totalCharged ?? charge.total)
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FleetBookingsTable;
